import React, { useState, useEffect } from "react";
import { Form, Row, Col, Table } from "react-bootstrap";
import { useNavigate, Link } from "react-router-dom";
import { useDispatch, } from "react-redux";
import { getExam } from "../../store/slices/exam";
import { fetchStandardSection } from "../../store/slices/getstandardsection";
import { toast } from "react-hot-toast";
import {
  FiHome,
  FiChevronRight,
} from "react-icons/fi";
import { setMarks } from "../../store/slices/setmarks";
import { getExamSubject } from "../../store/slices/subject";


const getAcademicSession = () => {
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth(); // Jan = 0, March = 2, April = 3

  // January, February, March
  if (currentMonth <= 2) {
    return `${currentYear - 1}-${String(currentYear).slice(-2)}`;
  }

  // April to December
  return `${currentYear}-${String(currentYear + 1).slice(-2)}`;
};


export default function AddSetMarks() {

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    session: getAcademicSession(),
    standardId: "",
    examId: "",
    subjects: [],
  });

  const [standards, setStandards] = useState([]);
  const [exams, setExams] = useState([]);
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    dispatch(fetchStandardSection())
      .unwrap()
      .then((res) => setStandards(res?.data || []))
      .catch((err) => toast.error(err));

    dispatch(getExam())
      .unwrap()
      .then((res) => setExams(res?.data || []))
      .catch((err) => toast.error(err));
  }, [dispatch]);

  const validate = () => {
    const newErrors = {};

    if (!formData.standardId) {
      newErrors.standardId = "Please select standard";
    }

    if (!formData.examId) {
      newErrors.examId = "Please select exam";
    }

    if (formData.subjects.length === 0) {
      toast.error("Please load subjects first.");
      return false;
    }
    formData.subjects.forEach((subject, index) => {
      if (!subject.sequence || Number(subject.sequence) <= 0) {
        newErrors[`sequence${index}`] = "Enter valid sequence";
      }

      if (!subject.maxMarks || Number(subject.maxMarks) <= 0) {
        newErrors[`maxMarks${index}`] = "Enter valid maximum marks";
      }

      if (!subject.passMarks || Number(subject.passMarks) <= 0) {
        newErrors[`passMarks${index}`] = "Enter valid pass marks";
      }
      if (!subject.type) {
        newErrors[`type${index}`] = "Please select type";
      }

      // Pass marks should be less than maximum marks
      if (
        subject.maxMarks &&
        subject.passMarks &&
        Number(subject.passMarks) >= Number(subject.maxMarks)
      ) {
        newErrors[`passMarks${index}`] =
          "Pass marks must be less than maximum marks";
      }
    });
    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleLoadSubjects = () => {
    if (!formData.standardId || !formData.examId) {
      toast.error("Please select standard and exam");
      return;
    }

    setLoadingSubjects(true);

    dispatch(
      getExamSubject({
        session: formData.session,
        standardId: formData.standardId,
      })
    )
      .unwrap()
      .then((res) => {
        const classSubject = res?.data?.[0];

        if (!classSubject) {
          toast.error("No subjects found.");
          return;
        }

        const subjects = classSubject.subjectId.map((subject) => ({
          subjectId: subject._id,
          name: subject.name,
          type: "",
          sequence: "",
          maxMarks: "",
          passMarks: "",
        }));
        setFormData((prev) => ({
          ...prev,
          subjects,
        }));
      })
      .catch((err) => {
        toast.error(err || "Failed to load subjects");
      })
      .finally(() => {
        setLoadingSubjects(false);
      });
  };


  const handleSubjectChange = (index, field, value) => {
    const updated = [...formData.subjects];

    if (["sequence", "maxMarks", "passMarks"].includes(field)) {
      value = value.replace(/\D/g, "");
    }

    updated[index][field] = value;

    setFormData((prev) => ({
      ...prev,
      subjects: updated,
    }));

    setErrors((prev) => ({
      ...prev,
      [`${field}${index}`]: "",
    }));
  };


  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validate()) return;

    const payload = {
      session: formData.session,
      standardId: formData.standardId,
      examId: formData.examId,
      subjects: formData.subjects.map((subject) => ({
        subjectId: subject.subjectId,
        type: subject.type,
        sequence: Number(subject.sequence),
        maxMarks: Number(subject.maxMarks),
        passMarks: Number(subject.passMarks),
      })),
    };

    dispatch(setMarks(payload))
      .unwrap()
      .then(() => {
        toast.success("Marks set successfully");
        navigate(-1);
      })
      .catch((err) => toast.error(err));
  };

  return (
    <>
      <div className="page-header">
        <div className="breadcrumb-section">

          <div className="breadcrumb-left">
            <div className="home-icon">
              <Link to="/institute/home"><FiHome style={{ color: "white", marginTop: "-4px" }} /></Link>
            </div>

            <FiChevronRight className="separator" />

            <Link to="/institute/subjects">Subjects</Link>

            <FiChevronRight className="separator" />

            <span className="active">Add Subject</span>
          </div>

        </div>
      </div>
      <div className="acs-card">
        <Form onSubmit={handleSubmit}>
          <Row className="g-3">

            {/* Standard Dropdown */}

            <Col md={3} sm={12}>
              <Form.Group>
                <Form.Label>Session</Form.Label>
                <Form.Control
                  type="text"
                  name="session"
                  value={formData.session}
                  readOnly
                />


                {errors.session && (
                  <small className="text-danger">
                    {errors.session}
                  </small>
                )}
              </Form.Group>
            </Col>
            <Col md={3} sm={12}>
              <Form.Group>
                <Form.Label>Standard</Form.Label>
                <Form.Select
                  name="standardId"
                  value={formData.standardId}
                  onChange={handleChange}
                >
                  <option value="">Select Standard</option>

                  {standards.map((item) => (
                    <option
                      key={item.standardId._id}
                      value={item.standardId._id}
                    >
                      {item.standardId.name}
                    </option>
                  ))}
                </Form.Select>
                {errors.standardId && (
                  <small className="text-danger">
                    {errors.standardId}
                  </small>
                )}
              </Form.Group>
            </Col>
            <Col md={3}>

              <Form.Label>Exam</Form.Label>

              <Form.Select
                name="examId"
                value={formData.examId}
                onChange={handleChange}
                disabled={!formData.standardId}
              >
                <option value="">Select Exam</option>

                {exams.map((exam) => (
                  <option key={exam._id} value={exam._id}>
                    {exam.name}
                  </option>
                ))}
              </Form.Select>

              {errors.examId && (
                <small className="text-danger">
                  {errors.examId}
                </small>
              )}
            </Col>
            <Col md={2} className="d-flex align-items-end">
              <button
                type="button"
                className="btn-submit w-100"
                onClick={handleLoadSubjects}
                disabled={loadingSubjects}
              >
                {loadingSubjects ? "Fetching..." : "Fetch Subjects"}
              </button>
            </Col>

          </Row>

          {formData.subjects.length > 0 && (
            <>
              <Table className="staff-table mt-5">
                <thead>
                  <tr>
                    <th className="text-center">#</th>
                    <th className="text-center">Subject</th>
                    <th className="text-center">Type</th>
                    <th className="text-center">Sequence</th>
                    <th className="text-center">Maximum Marks</th>
                    <th className="text-center">Pass Marks</th>
                  </tr>
                </thead>

                <tbody>
                  {formData.subjects.map((subject, index) => (
                    <tr key={subject.subjectId}>
                      <td className="text-center">{index + 1}</td>

                      <td className="text-center">{subject.name}</td>

                      <td className="text-center">
                        <Form.Select
                          value={subject.type}
                          onChange={(e) =>
                            handleSubjectChange(
                              index,
                              "type",
                              e.target.value
                            )
                          }
                        >
                          <option value="">Select</option>
                          <option value="Mark">Mark</option>
                          <option value="Grade">Grade</option>
                        </Form.Select>

                        {errors[`type${index}`] && (
                          <small className="text-danger">
                            {errors[`type${index}`]}
                          </small>
                        )}
                      </td>

                      <td className="text-center">
                        <Form.Control
                          type="text" maxLength={2}
                          inputMode="numeric"
                          value={subject.sequence}
                          onChange={(e) =>
                            handleSubjectChange(
                              index,
                              "sequence",
                              e.target.value
                            )
                          }
                        />
                        {errors[`sequence${index}`] && (
                          <small className="text-danger">
                            {errors[`sequence${index}`]}
                          </small>
                        )}
                      </td>

                      <td className="text-center">
                        <Form.Control
                          type="text" maxLength={3}
                          inputMode="numeric"
                          value={subject.maxMarks}
                          onChange={(e) =>
                            handleSubjectChange(
                              index,
                              "maxMarks",
                              e.target.value
                            )
                          }
                        />
                        {errors[`maxMarks${index}`] && (
                          <small className="text-danger">
                            {errors[`maxMarks${index}`]}
                          </small>
                        )}
                      </td>

                      <td className="text-center">
                        <Form.Control
                          type="text" maxLength={2}
                          inputMode="numeric"
                          value={subject.passMarks}
                          onChange={(e) =>
                            handleSubjectChange(
                              index,
                              "passMarks",
                              e.target.value
                            )
                          }
                        />
                        {errors[`passMarks${index}`] && (
                          <small className="text-danger">
                            {errors[`passMarks${index}`]}
                          </small>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              <div className="sa-actions mt-4">
                <button
                  type="button"
                  className="btn-cancel"
                  style={{ color: "black" }}
                  onClick={() => navigate(-1)}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="btn-submit"
                >
                  Submit
                </button>
              </div>
            </>
          )}
        </Form>
      </div>
    </>
  );
}
